import { useMemo } from 'react';

import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useEntryRules } from '@/feature/betslip-pickem/hooks/use-entry-rules';
import {
    CriterionType,
    EntryPromotion,
    EntryPromotionError,
    EntryPromotionStatus,
    GameMode,
    PromotionType,
} from '@/types/api.generated';
import { EntryErrorCodes } from '@/utils/errors/entry-errors';

import { BetValidationData } from '../api/types';
import { BetslipPick } from '../types';

export type FantasyEntryPromotionStatus = Omit<EntryPromotionStatus, 'promotion'> & {
    promotion: Omit<EntryPromotion, 'type'> & {
        type: PromotionType | 'MYSTERY';
    };
};

type FantasyEntryPromotions = {
    /**
     * List of promotions available for the current game mode.
     */
    entryPromotions: FantasyEntryPromotionStatus[];
};

/**
 * Hook to retrieve and manage available entry promotions for the active game mode and determine if an auto-apply promotion should be applied.
 * @returns {FantasyEntryPromotions} Object containing available promotions, auto-apply logic, and the promotion to auto-apply
 */
export const useFantasyEntryPromotions = (): FantasyEntryPromotions => {
    const activeGameMode = useBetslipStore(state => state.activeGameMode);
    const validationData = useBetslipStore(state => state.validationData);
    const betslip = useBetslipStore(state => state.betslip);

    const { promotionsMap } = useEntryRules();

    const entryPromotions = useMemo(() => {
        return computeEntryPromotions({
            activeGameMode,
            betslip,
            activeGameModePromotions: promotionsMap[activeGameMode],
            mysteryBetValidationData: validationData.MYSTERY.betValidation,
        });
    }, [activeGameMode, betslip, promotionsMap, validationData.MYSTERY.betValidation]);

    return {
        entryPromotions,
    };
};

export const getMysteryPromoData = (
    betslip: BetslipPick[],
    mysteryBetValidation?: BetValidationData
): FantasyEntryPromotionStatus | undefined => {
    if (!mysteryBetValidation) {
        return undefined;
    }
    //Amount errors not considered as errors, as they can be fixed by changing the amount
    const mysteryErrors = mysteryBetValidation.errors.filter(it => it.code !== EntryErrorCodes.INVALID_AMOUNT) ?? [];

    const exceedsPicksCount = mysteryBetValidation.errors.some(
        it => it.code === EntryErrorCodes.MYSTERY_ERROR_EXCEEDED_PICKS_COUNT
    );

    //Per requirements, we hide mystery mode if the user has exceeded the picks count
    //but show it for other errors, and use the short description to show the error
    const showMysteryPromo = !exceedsPicksCount;
    if (showMysteryPromo) {
        const mysteryPromoDescription = `2 regular picks to win ${mysteryBetValidation.minMysteryMultiplier}x-${mysteryBetValidation?.maxMysteryMultiplier}x`;
        let mysteryPromoError: EntryPromotionError | undefined;
        if (mysteryErrors.length > 0) {
            //Any other error will be shown as the short description
            mysteryPromoError = {
                title: 'Mystery Promo Error',
                shortDescription: mysteryErrors[0].message,
                longDescription: '',
                order: 0,
            };
        }

        return {
            promotion: {
                id: 'mystery',
                name: 'Mystery Multiplier',
                description: mysteryPromoDescription,
                type: 'MYSTERY',
                autoApply: false,
                criteria: [{ type: CriterionType.CashOnly, value: 'true' }],
                metadata: [],
            },
            errors: mysteryPromoError ? [mysteryPromoError] : undefined,
        };
    } else {
        return undefined;
    }
};

export const computeEntryPromotions = ({
    activeGameMode,
    betslip,
    activeGameModePromotions,
    mysteryBetValidationData,
}: {
    activeGameMode: GameMode;
    betslip: BetslipPick[];
    activeGameModePromotions: FantasyEntryPromotionStatus[];
    mysteryBetValidationData: BetValidationData | undefined;
}) => {
    const computedPromos = [...activeGameModePromotions];
    // Mystery only available as Perfect play
    const hasPerfectGameMode = activeGameMode === GameMode.Perfect;
    if (hasPerfectGameMode) {
        const mysteryPromo = getMysteryPromoData(betslip, mysteryBetValidationData);
        if (mysteryPromo) {
            computedPromos.push(mysteryPromo);
        }
    }
    return computedPromos;
};
